/**
 * Every user-facing label for a backend code, in one place.
 *
 * The outreacher flow is the spine of the vocabulary:
 *
 *   Found → Messaged → Replied → Joined → Posted
 *
 * - Messaged: a DM was sent. Not a log; nothing is owed.
 * - Replied: the creator replied asking to join. This IS the log (an invite
 *   row with proof), and the only stage a log bonus can apply to.
 * - Joined / Posted: confirmed by clipping (or an admin, for teams on
 *   manual tracking).
 *
 * Off the path: Expired (past the re-log window without joining), Not paid
 * (rejected by an admin) and Hidden (soft-deleted).
 *
 * Rules: never render a raw code. Pills, filters, table headers, toasts and
 * CSV headers all read from here. Rename a label here, nowhere else.
 */
import type { PillTone } from '@/components/ui/pill';
import type { OutreachInvite } from '@/api/types';

/** "SOCIAL_MEDIA" → "Social media"; "micro_ugc" → "Micro ugc". Last-resort fallback only. */
export function titleCase(code: string | null | undefined): string {
  if (!code) return '';
  const s = code.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function lookup(map: Record<string, string>, code: string | null | undefined): string {
  if (!code) return '';
  return map[code] ?? titleCase(code);
}

// ── Invite stage ──────────────────────────────────────────────────────────

export type InviteStageKey =
  | 'replied'
  | 'manual'
  | 'joined'
  | 'posted'
  | 'expired'
  | 'notPaid'
  | 'hidden';

export interface InviteStage {
  key: InviteStageKey;
  label: string;
  tone: PillTone;
  /** One sentence for a tooltip: what this state means and what happens next. */
  hint: string;
}

export const INVITE_STAGES: Record<InviteStageKey, Omit<InviteStage, 'key'>> = {
  replied: {
    label: 'Replied',
    tone: 'pending',
    hint: 'The creator replied and this was logged. Waiting for them to join.',
  },
  manual: {
    label: 'Manual tracking',
    tone: 'muted',
    hint: "This team isn't checked automatically with clipping. An admin updates joins and posts.",
  },
  joined: {
    label: 'Joined',
    tone: 'verified',
    hint: 'The creator joined the clipping platform. Waiting for their first approved post.',
  },
  posted: {
    label: 'Posted',
    tone: 'paid',
    hint: 'The creator posted an approved clip for a campaign.',
  },
  expired: {
    label: 'Expired',
    tone: 'muted',
    hint: "The creator didn't join within the re-log window, so anyone can log them again. No longer checked.",
  },
  notPaid: {
    label: 'Not paid',
    tone: 'rejected',
    hint: 'Rejected by an admin. Anything still owed on it has been written off.',
  },
  hidden: {
    label: 'Hidden',
    tone: 'destructive',
    hint: 'Removed by the outreacher. Still visible to admins; any amount owed stays owed.',
  },
};

type StageInput = Pick<OutreachInvite, 'status'> &
  Partial<Pick<OutreachInvite, 'deletedAt'>> & {
    releasedAt?: string | null;
  };

/**
 * Where an invite sits in the flow, as people should read it.
 *
 * `teamTracks` is the inviter's team `clippingResolveRequired` setting.
 * Leave it undefined when unknown: the invite then reads "Replied" rather
 * than claiming manual tracking.
 */
export function inviteStage(invite: StageInput, opts: { teamTracks?: boolean } = {}): InviteStage {
  const key = inviteStageKey(invite, opts);
  return { key, ...INVITE_STAGES[key] };
}

export function inviteStageKey(invite: StageInput, opts: { teamTracks?: boolean } = {}): InviteStageKey {
  if (invite.deletedAt) return 'hidden';
  switch (invite.status) {
    case 'REJECTED':
      return 'notPaid';
    case 'CONVERTED':
      return 'posted';
    case 'VERIFIED':
      return 'joined';
    default:
      if (invite.releasedAt) return 'expired';
      if (opts.teamTracks === false) return 'manual';
      return 'replied';
  }
}

/** Raw invite status → stage label, for filters and charts that only have the status. */
export const INVITE_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Replied',
  VERIFIED: 'Joined',
  CONVERTED: 'Posted',
  REJECTED: 'Not paid',
};
export const inviteStatusLabel = (s: string | null | undefined): string => lookup(INVITE_STATUS_LABELS, s);

/** The flow, in order, for funnels and stage pickers. */
export const FLOW_STAGES = ['Messaged', 'Replied', 'Joined', 'Posted'] as const;

// ── Other statuses ────────────────────────────────────────────────────────

export const PAYOUT_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Queued',
  SUBMITTED: 'Sent to payment provider',
  PAID: 'Paid',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};
export const payoutStatusLabel = (s: string | null | undefined): string => lookup(PAYOUT_STATUS_LABELS, s);

/** Discovery queue (prospect) states, same words on admin, outreacher and discovery surfaces. */
export const PROSPECT_STATUS_LABELS: Record<string, string> = {
  AVAILABLE: 'Open',
  CLAIMED: 'Claimed',
  CONTACTED: 'Messaged',
  PROMOTED: 'Logged',
  DISMISSED: 'Removed',
};
export const prospectStatusLabel = (s: string | null | undefined): string => lookup(PROSPECT_STATUS_LABELS, s);

export const VERDICT_LABELS: Record<string, string> = {
  APPROVED: 'Proof OK',
  REJECTED: 'Proof fails',
  UNCLEAR: "Can't tell",
};
export const verdictLabel = (v: string | null | undefined): string => (v ? lookup(VERDICT_LABELS, v) : 'Not reviewed');
export const verdictTone = (v: string | null | undefined): PillTone =>
  v === 'APPROVED' ? 'success' : v === 'REJECTED' ? 'rejected' : 'muted';

export const REQUEST_STATUS_LABELS: Record<string, string> = {
  REQUESTED: 'Requested',
  IN_PROGRESS: 'In progress',
  AWAITING_RESPONSE: 'Waiting on creator',
  AWAITING_RATE: 'Waiting on rate approval',
  ROSTERED: 'Rostered',
  CANCELLED: 'Cancelled',
  COMPLETED: 'Completed',
};
export const requestStatusLabel = (s: string | null | undefined): string => lookup(REQUEST_STATUS_LABELS, s);

export const CANCELLATION_REASON_LABELS: Record<string, string> = {
  GHOSTED: 'Creator went silent',
  TOO_EXPENSIVE: 'Too expensive',
  REFUSING_PROMOS: "Won't do promos",
  OUT_OF_NICHE: 'Outside the niche',
  OTHER: 'Other',
};
export const cancellationReasonLabel = (s: string | null | undefined): string =>
  lookup(CANCELLATION_REASON_LABELS, s);

export const ACCESS_REQUEST_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Awaiting approval',
  APPROVED: 'Approved',
  REJECTED: 'Declined',
};
export const accessRequestStatusLabel = (s: string | null | undefined): string =>
  lookup(ACCESS_REQUEST_STATUS_LABELS, s);

// ── Config vocabulary ─────────────────────────────────────────────────────

/** Category → "Creator type": the rate-card tier. */
export const CREATOR_TYPE_LABELS: Record<string, string> = {
  CLIPPER: 'Clipper',
  MICRO_UGC: 'Micro UGC',
  INFLUENCER: 'Influencer',
};
export const creatorTypeLabel = (c: string | null | undefined): string => lookup(CREATOR_TYPE_LABELS, c);

export const RATE_TYPE_LABELS: Record<string, string> = {
  LOG_BONUS: 'When logged',
  JOIN_BONUS: 'When joined',
  POST_BASE: 'Per approved post',
  NICHE_BONUS: 'Niche bonus',
  INFLUENCER_FLAT: 'Influencer flat rate',
  HIGH_FOLLOWER_POST: 'High-follower post',
};
export const rateTypeLabel = (t: string | null | undefined): string => lookup(RATE_TYPE_LABELS, t);

export const PAY_MODEL_LABELS: Record<string, string> = {
  PER_CONVERSION: 'Rate card',
  RETAINER: 'Weekly retainer',
};
export const payModelLabel = (m: string | null | undefined): string => lookup(PAY_MODEL_LABELS, m);

/** Fallback team names for surfaces without the teams list. Prefer the API's displayName. */
export const TEAM_LABELS: Record<string, string> = {
  SOCIAL_MEDIA: 'Social Media',
  DISCORD: 'Discord',
  VYRAL: 'Vyral',
  INFLUENCER: 'Influencer',
};
export const teamLabel = (code: string | null | undefined, displayName?: string | null): string =>
  displayName || lookup(TEAM_LABELS, code);

export const PROVIDER_LABELS: Record<string, string> = {
  clipping: 'Clipping',
  financial: 'Spade Financial',
  local: 'Record only',
};
export const providerLabel = (p: string | null | undefined): string => lookup(PROVIDER_LABELS, p);

// ── Glossary (for page help) ──────────────────────────────────────────────

export const GLOSSARY = {
  invite: 'A creator who replied asking to join, logged with proof.',
  messaged: 'An outreacher sent a DM. Not a log; nothing is owed.',
  earned: 'The value credited to an invite so far (log, join, post and follower bonuses).',
  owedBeforeAdjustments: 'Earned minus paid. Adjustments and write-offs are not subtracted here.',
  outstanding: 'Everything still owed across all periods, not just this week.',
  creditWindow: 'When a join or post counts for an invite: from shortly before it was logged onward.',
  reLogWindow: "Days after logging before an invite that hasn't joined expires and anyone can log the creator again.",
} as const;
